import { type ReactNode } from "react";
import {
  DashboardGrid,
  KpiCard,
  type KpiCardProps,
} from "../../components/observability/index.ts";
import { defaultNumberFormat, type Measure } from "../../components/index.ts";
import type { EnforcementSummary } from "./use-enforcement.ts";
import { ASSERTION_FAILURE_CODE, quantityMeasure } from "./vocab.ts";

/**
 * The Enforcement headline KPIs (observability-ui task 4.1).
 *
 * The Enforcement summary carries NO time-series (its three regions are all catalog-complete,
 * point-in-time breakdowns), so there is nothing to fold into a `ChartCard` here — doing so would
 * mean inventing a series, which the rollout forbids. What IS natural and honest is a headline KPI
 * row, so this re-homes the screen's existing summary onto the observability kit's `KpiCard`s in a
 * `DashboardGrid`, matching the Overview composition pattern. This is PRESENTATION-ONLY: every KPI
 * is derived from the SAME `EnforcementSummary` the tables below already render, using the screen's
 * own vocab helpers — no new read, no fabricated value.
 *
 * The four tiles are the window's at-a-glance totals, each a sum over the summary's own counts:
 * total verification failures (every verification outcome whose code is not `none`), the
 * assertion-failure subset of those (`invalid_assertion`), total replay detections across every
 * namespace, and total Gateway pipeline failures across every stage. Each tile carries its honest
 * confidence (design 12.3): a tile is `estimate` when any contributing count was sampled, and
 * `unverifiable` when any contributing count was null (no data) — so a count the server could not
 * verify is never shown as an exact, reassuring number. The tables below remain the authoritative,
 * drillable breakdowns; these tiles only summarise them.
 *
 * Theme-aware via the kit's tokens (Requirement 2.1); the whole row is a labelled dashboard region
 * (Requirement 1.3). It renders exactly what the summary provides and invents nothing.
 */

export interface EnforcementKpisProps {
  readonly summary: EnforcementSummary;
}

/** A KpiCard plus a stable React key, so the row can map a list without inventing keys. */
interface KeyedKpi {
  readonly key: string;
  readonly props: KpiCardProps;
}

/**
 * Combine the confidence of several contributing `Measure`s into one: `unverifiable` wins (any null
 * contributor means the total cannot be trusted), then `estimate` (any sampled contributor), else
 * `exact`. Mirrors how the token-denial aggregate in Identity combines confidence.
 */
function combineMeasure(measures: readonly Measure[]): Measure {
  if (measures.some((m) => m === "unverifiable")) return "unverifiable";
  if (measures.some((m) => m === "estimate")) return "estimate";
  return "exact";
}

export function EnforcementKpis(props: EnforcementKpisProps): ReactNode {
  const { summary } = props;

  // Verification failures: every observed verification outcome whose code is a real failure (not the
  // `none` success label). Sum the values; combine the per-row confidence into the tile's measure.
  const failureRows = summary.resource_verification.filter((r) => r.code !== "none");
  const verificationFailures = sumValues(failureRows.map((r) => r.count.value));
  const verificationMeasure = combineMeasure(failureRows.map((r) => quantityMeasure(r.count)));

  // Assertion failures: the `invalid_assertion` subset of the verification outcomes (design 12.2).
  const assertionRows = summary.resource_verification.filter(
    (r) => r.code === ASSERTION_FAILURE_CODE,
  );
  const assertionFailures = sumValues(assertionRows.map((r) => r.count.value));
  const assertionMeasure = combineMeasure(assertionRows.map((r) => quantityMeasure(r.count)));

  // Replay detections: the sum across every namespace's always-present count.
  const replayDetections = sumValues(summary.replay_by_namespace.map((r) => r.count.value));
  const replayMeasure = combineMeasure(
    summary.replay_by_namespace.map((r) => quantityMeasure(r.count)),
  );

  // Gateway failures: the sum of every stage's always-present total.
  const gatewayFailures = sumValues(summary.gateway_stage_failures.map((s) => s.total.value));
  const gatewayMeasure = combineMeasure(
    summary.gateway_stage_failures.map((s) => quantityMeasure(s.total)),
  );

  const kpis: readonly KeyedKpi[] = [
    {
      key: "verification",
      props: kpiProps("Verification failures", verificationFailures, verificationMeasure),
    },
    {
      key: "assertion",
      props: kpiProps("Assertion failures", assertionFailures, assertionMeasure),
    },
    {
      key: "replay",
      props: kpiProps("Replay detections", replayDetections, replayMeasure),
    },
    {
      key: "gateway",
      props: kpiProps("Gateway failures", gatewayFailures, gatewayMeasure),
    },
  ];

  return (
    <DashboardGrid
      label="Enforcement summary"
      className="enf-kpis"
      kpis={kpis.map((kpi) => (
        <KpiCard key={kpi.key} {...kpi.props} />
      ))}
    />
  );
}

/**
 * Build a KpiCard's props for a derived total. When every contributing count was null the tile has
 * no verifiable value, so it renders the kit's honest empty state (a "no reading" tile) rather than
 * a fabricated zero; otherwise it shows the summed count, formatted, with its combined confidence.
 */
function kpiProps(label: string, value: number | null, measure: Measure): KpiCardProps {
  if (value === null) {
    return { label, value: "n/a", empty: true, emptyHint: "No reading in this window." };
  }
  return { label, value: defaultNumberFormat(value), measure };
}

/**
 * Sum a set of `Quantity` values, treating a sampled/exact number as a contributor and a null (no
 * data) as "nothing to add". Returns `null` only when EVERY contributor was null (so the whole total
 * is unverifiable); otherwise the sum of the present values. This keeps a tile honest: a window with
 * some real counts and some nulls still shows the real sum, while a window with nothing but nulls
 * shows the empty state rather than a misleading zero.
 */
function sumValues(values: readonly (number | null)[]): number | null {
  let total = 0;
  let sawValue = false;
  for (const v of values) {
    if (v === null) continue;
    total += v;
    sawValue = true;
  }
  return sawValue ? total : null;
}
